import { HttpClient } from '@angular/common/http';
import { Injectable, signal, computed, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, tap, catchError, of, map, Subject, filter, take, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

const TOKEN_KEY = 'mm_aggregator_admin_token';
const USER_KEY = 'mm_aggregator_admin_user';
const TOKEN_EXPIRES_KEY = 'mm_aggregator_admin_token_expires';
const ACTIVE_ASSIGNMENT_KEY = 'mm_aggregator_admin_active_assignment';

/** Refresh the token when less than this many ms remain. */
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes before expiry

/** Minimum interval between two refresh attempts (avoids tight loops). */
const MIN_REFRESH_INTERVAL_MS = 30 * 1000; // 30 seconds

export interface CurrentUser {
  id: string | number;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
  is_active?: boolean;
  email_verified?: boolean;
  /** Temporary password or password reset: every API call except changing the password is refused until done. */
  password_change_required?: boolean;
  /** Signed in through a password-reset code: the new password needs no current password. */
  password_recovery_session?: boolean;
  /** Active role codes from UserRole assignments (API: `active_roles`). */
  active_roles?: string[];
  /** Highest-priority role for dashboards/menus (API: `effective_role`). */
  effective_role?: string | null;
  /** `SYSTEM` or `MERCHANT` for `effective_role` (API: `effective_role_type`). */
  effective_role_type?: string | null;
  preferred_language?: 'en' | 'fr';
  role_assignments?: RoleAssignment[];
  active_assignment?: ActiveAssignment | null;
}

export interface RoleAssignment {
  user_role_id: string;
  role_code: string;
  role_type: string;
  merchant_id: string | null;
  merchant_name: string | null;
  merchant_logo: string | null;
}

export interface ActiveAssignment {
  role_code: string | null;
  role_type: string | null;
  merchant_id: string | null;
  merchant_name: string | null;
  merchant_logo: string | null;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  expires_at: string;
  user: CurrentUser;
}

export interface LoginOtpRequiredResponse {
  otp_required: true;
  otp_id: string;
  channel: string;
  masked_destination: string;
}

export type LoginResult =
  | { kind: 'authenticated'; user: CurrentUser }
  | { kind: 'otp_required'; otp_id: string; channel: string; masked_destination: string };

export interface PasswordResetRequestResponse {
  otp_id: string | null;
  channel: string;
  masked_destination: string;
  detail?: string;
}

export interface EmailVerificationResendResponse {
  detail: string;
  otp_id: string;
  channel: string;
  masked_destination: string;
}

function loadStoredToken(): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY);

    if (!token || !expiresAt) return null;

    // Check if token is expired
    const expiresDate = new Date(expiresAt);
    if (new Date() >= expiresDate) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRES_KEY);
      localStorage.removeItem(USER_KEY);
      return null;
    }

    return token;
  } catch {
    return null;
  }
}

function loadStoredUser(): CurrentUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as CurrentUser;
    return data?.email ? data : null;
  } catch {
    return null;
  }
}

function saveToken(token: string | null, expiresAt: string | null): void {
  try {
    if (token && expiresAt) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(TOKEN_EXPIRES_KEY, expiresAt);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRES_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

function saveStoredUser(user: CurrentUser | null): void {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    // ignore storage errors
  }
}

function loadStoredActiveAssignment(): ActiveAssignment | null {
  try {
    const raw = localStorage.getItem(ACTIVE_ASSIGNMENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ActiveAssignment;
  } catch {
    return null;
  }
}

function saveStoredActiveAssignment(activeAssignment: ActiveAssignment | null): void {
  try {
    if (activeAssignment) localStorage.setItem(ACTIVE_ASSIGNMENT_KEY, JSON.stringify(activeAssignment));
    else localStorage.removeItem(ACTIVE_ASSIGNMENT_KEY);
  } catch {
    // ignore storage errors
  }
}

/**
 * Authentication service for token-based auth with the Django backend.
 *
 * - Token and user are persisted in localStorage.
 * - A background timer proactively refreshes the token before it expires.
 * - An observable (`refreshToken$`) is exposed for the interceptor to retry
 *   failed 401 requests after a successful refresh.
 * - Only one refresh request runs at a time (concurrent callers share it).
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private readonly currentUser = signal<CurrentUser | null>(null);
  private readonly accessToken = signal<string | null>(null);
  private readonly loading = signal(false);

  readonly user = computed(() => this.currentUser());
  readonly isAuthenticated = computed(() => this.accessToken() !== null && this.currentUser() !== null);
  readonly isLoading = computed(() => this.loading());
  readonly needsEmailVerification = computed(() => {
    const user = this.currentUser();
    if (!user) return false;
    return user.email_verified === false;
  });

  /** Emits `true` when a refresh succeeds, `false` when it fails. */
  private readonly refreshResult$ = new Subject<boolean>();

  /** Whether a refresh HTTP call is currently in-flight. */
  private refreshInProgress = false;

  /** Handle for the proactive refresh timer (setTimeout ID). */
  private refreshTimerHandle: ReturnType<typeof setTimeout> | null = null;

  /** Timestamp of the last refresh attempt (guards against tight loops). */
  private lastRefreshAttemptMs = 0;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {
    // Load stored token and user on initialization
    const storedToken = loadStoredToken();
    const storedUser = loadStoredUser();
    const storedAssignment = loadStoredActiveAssignment();

    if (storedToken && storedUser) {
      this.accessToken.set(storedToken);
      this.currentUser.set(
        storedAssignment ? { ...storedUser, active_assignment: storedAssignment } : storedUser
      );
      this.scheduleRefresh();
    }
  }

  ngOnDestroy(): void {
    this.clearRefreshTimer();
  }

  /** Get the current access token. */
  getToken(): string | null {
    return this.accessToken();
  }

  /** Whether a token refresh is currently in-flight. */
  get isRefreshing(): boolean {
    return this.refreshInProgress;
  }

  /**
   * Returns an Observable that completes with `true` once the in-flight
   * refresh succeeds (or `false` if it fails).  Used by the interceptor to
   * wait and then retry the original request.
   */
  waitForRefresh(): Observable<boolean> {
    return this.refreshResult$.pipe(
      take(1)
    );
  }

  /**
   * Resolve current user for guards: use stored user if present, otherwise fetch once.
   */
  resolveUser(): Observable<CurrentUser | null> {
    const existing = this.currentUser();
    const token = this.accessToken();

    if (existing && token) return of(existing);
    if (!token) return of(null);

    return this.fetchCurrentUser();
  }

  /** Fetch current user from API (GET .../auth/me/). */
  fetchCurrentUser(): Observable<CurrentUser | null> {
    const token = this.accessToken();
    if (!token) {
      this.currentUser.set(null);
      return of(null);
    }

    this.loading.set(true);
    const url = `${environment.apiUrl}/auth/me/`;

    return this.http.get<CurrentUser>(url).pipe(
      tap((user) => {
        this.currentUser.set(user);
        saveStoredUser(user);
        this.loading.set(false);
      }),
      catchError(() => {
        this.clearLocalState();
        this.loading.set(false);
        return of(null);
      })
    );
  }

  /** Login with username and password (supports OTP challenge step). */
  login(username: string, password: string, turnstileToken = ''): Observable<LoginResult | null> {
    this.loading.set(true);
    const url = `${environment.apiUrl}/auth/login/`;

    return this.http
      .post<LoginResponse | LoginOtpRequiredResponse>(url, {
        username,
        password,
        turnstile_token: turnstileToken,
      })
      .pipe(
        map((response) => {
          if ('otp_required' in response && response.otp_required) {
            this.loading.set(false);
            return {
              kind: 'otp_required' as const,
              otp_id: response.otp_id,
              channel: response.channel,
              masked_destination: response.masked_destination,
            };
          }
          const authResponse = response as LoginResponse;
          this.applyTokenResponse(authResponse);
          this.loading.set(false);
          return { kind: 'authenticated' as const, user: authResponse.user };
        }),
        catchError((err: HttpErrorResponse) => {
          this.loading.set(false);
          return throwError(() => err);
        }),
      );
  }

  /** Ask for a password-reset code by email (same answer whether or not the account exists). */
  requestPasswordReset(email: string, turnstileToken: string): Observable<PasswordResetRequestResponse> {
    return this.http.post<PasswordResetRequestResponse>(`${environment.apiUrl}/auth/password/forgot/`, {
      email,
      turnstile_token: turnstileToken,
    });
  }

  /** Validate the reset code: signs in with a restricted session that can only set a new password. */
  verifyPasswordReset(otpId: string, code: string, turnstileToken: string): Observable<CurrentUser> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/auth/password/verify/`, {
        otp_id: otpId,
        code,
        turnstile_token: turnstileToken,
      })
      .pipe(
        map((response) => {
          this.applyTokenResponse(response);
          return response.user;
        }),
      );
  }

  verifyLoginOtp(otpId: string, code: string): Observable<CurrentUser | null> {
    this.loading.set(true);
    const url = `${environment.apiUrl}/auth/login/verify-otp/`;
    return this.http.post<LoginResponse>(url, { otp_id: otpId, code }).pipe(
      map((response) => {
        this.applyTokenResponse(response);
        this.loading.set(false);
        return response.user;
      }),
      catchError((err: HttpErrorResponse) => {
        this.loading.set(false);
        return throwError(() => err);
      })
    );
  }

  resendEmailVerification(): Observable<EmailVerificationResendResponse | null> {
    const token = this.accessToken();
    if (!token) return of(null);
    return this.http.post<EmailVerificationResendResponse>(`${environment.apiUrl}/auth/email/resend-verification/`, {}).pipe(
      catchError(() => of(null))
    );
  }

  verifyEmail(otpId: string, code: string): Observable<boolean> {
    const token = this.accessToken();
    if (!token) return of(false);
    return this.http.post<{ detail: string }>(`${environment.apiUrl}/auth/email/verify/`, { otp_id: otpId, code }).pipe(
      map(() => {
        const user = this.currentUser();
        if (user) {
          const updated = { ...user, email_verified: true };
          this.currentUser.set(updated);
          saveStoredUser(updated);
        }
        return true;
      }),
      catchError(() => of(false))
    );
  }

  switchActiveRole(roleCode: string, merchantId: string | null): Observable<CurrentUser | null> {
    return this.http
      .post<CurrentUser>(`${environment.apiUrl}/auth/active-role/`, {
        role_code: roleCode,
        merchant_id: merchantId,
      })
      .pipe(
        map((user) => {
          this.currentUser.set(user);
          saveStoredUser(user);
          saveStoredActiveAssignment(user.active_assignment ?? null);
          return user;
        }),
        catchError(() => of(null))
      );
  }

  /** Logout: revoke token on server and clear local state. */
  logout(): void {
    const token = this.accessToken();

    // Clear local state & timer immediately
    this.clearLocalState();
    this.clearRefreshTimer();

    if (token) {
      const url = `${environment.apiUrl}/auth/logout/`;
      this.http.post(url, {}).subscribe({
        next: () => this.navigateToLogin(),
        error: () => this.navigateToLogin(),
      });
    } else {
      this.navigateToLogin();
    }
  }

  /**
   * Tell the backend the interactive session is still active (PCI idle clock).
   * Fire-and-forget; failures are ignored (401 will clear session via interceptors).
   */
  pingSessionActivity(): void {
    if (!this.accessToken()) {
      return;
    }
    this.http.post(`${environment.apiUrl}/auth/session/activity/`, {}).subscribe({
      error: () => undefined,
    });
  }

  /**
   * Attempt to refresh the token.
   *
   * - If a refresh is already in progress, returns an observable that waits
   *   for the existing one to finish.
   * - Revokes the old token server-side and stores the new one.
   * - Returns an Observable<boolean> (true = success).
   */
  refreshToken(): Observable<boolean> {
    // If already refreshing, just wait for the result
    if (this.refreshInProgress) {
      return this.waitForRefresh();
    }

    const token = this.accessToken();
    if (!token) {
      return of(false);
    }

    // Rate-limit: prevent tight loops
    const now = Date.now();
    if (now - this.lastRefreshAttemptMs < MIN_REFRESH_INTERVAL_MS) {
      return of(false);
    }
    this.lastRefreshAttemptMs = now;

    this.refreshInProgress = true;
    const url = `${environment.apiUrl}/auth/refresh/`;

    return this.http.post<LoginResponse>(url, {}).pipe(
      map((response) => {
        this.applyTokenResponse(response);
        this.refreshInProgress = false;
        this.refreshResult$.next(true);
        return true;
      }),
      catchError(() => {
        // Refresh failed (token already expired or revoked)
        this.refreshInProgress = false;
        this.refreshResult$.next(false);
        return of(false);
      })
    );
  }

  /** Clear local auth state (token, user). */
  clearLocalState(): void {
    this.accessToken.set(null);
    this.currentUser.set(null);
    saveToken(null, null);
    saveStoredUser(null);
    saveStoredActiveAssignment(null);
    this.clearRefreshTimer();
  }

  /** Clear all local data. */
  clearAllData(): void {
    this.clearLocalState();
    try {
      sessionStorage.clear();
    } catch {
      // Ignore errors
    }
  }

  /** Set user (e.g. after login response). */
  setUser(user: CurrentUser | null): void {
    this.currentUser.set(user);
    saveStoredUser(user);
    saveStoredActiveAssignment(user?.active_assignment ?? null);
  }

  // ── Private helpers ──────────────────────────────────────────

  /** Apply a login/refresh response: store token, user, and schedule next refresh. */
  private applyTokenResponse(response: LoginResponse): void {
    this.accessToken.set(response.access_token);
    this.currentUser.set(response.user);
    saveToken(response.access_token, response.expires_at);
    saveStoredUser(response.user);
    saveStoredActiveAssignment(response.user.active_assignment ?? null);
    this.scheduleRefresh();
  }

  /**
   * Schedule a proactive background refresh.
   *
   * Computes how many ms until REFRESH_THRESHOLD_MS before expiry, then
   * sets a single `setTimeout`.  Only one timer is active at a time.
   */
  private scheduleRefresh(): void {
    this.clearRefreshTimer();

    const expiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY);
    if (!expiresAt) return;

    const expiresMs = new Date(expiresAt).getTime();
    const refreshAt = expiresMs - REFRESH_THRESHOLD_MS;
    const delay = Math.max(refreshAt - Date.now(), 0);

    this.refreshTimerHandle = setTimeout(() => {
      this.refreshTimerHandle = null;
      // Only refresh if we still have a token
      if (this.accessToken()) {
        this.refreshToken().subscribe();
      }
    }, delay);
  }

  /** Clear the proactive refresh timer. */
  private clearRefreshTimer(): void {
    if (this.refreshTimerHandle !== null) {
      clearTimeout(this.refreshTimerHandle);
      this.refreshTimerHandle = null;
    }
  }

  /** Navigate to login page. */
  private navigateToLogin(): void {
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }
}
